import { useEffect, type ReactNode } from "react";
import {
  createFileRoute,
  redirect,
  Outlet,
  useLocation,
  useMatches,
  useNavigate,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { meQueryOptions } from "@/app/auth";
import { AuthError } from "@/lib/errors";
import ButtonComponent from "@/components/ButtonComponent";
import { useAuth, useSyncSentryUser } from "@/hooks/useAuth";
import { useSessionKeepAlive } from "@/hooks/useSessionKeepAlive";
import { useNavigationTracker } from "@/hooks/useNavigation";
import { CopilotProvider, useCopilot } from "@/components/Copilot/context";
import { CopilotPanel } from "@/components/Copilot";
import { GlobalErrorHandler } from "@/components/GlobalErrorHandler";
import { PageAccessGate } from "@/components/PageAccessGate";
import type { PageKey } from "@/hooks/api/useAccessManagement";
import Sidebar from "@/components/Sidebar";
import { SubrailSlotProvider } from "@/components/SidebarSubrailSlot";
import Header from "@/components/Header";
import { MobileSubnav } from "@/components/MobileSidebar";
import ChatBanner from "@/components/Support/ChatBanner";
import PageTransition from "@/components/PageTransition";
import { Toaster } from "@/components/ui/sonner";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { isSheetDetailPath } from "@/components/Sheets/paths";
import { cn } from "@/lib/utils";

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    /**
     * Access policy for an authenticated route. Routes are **admin-only by
     * default** (default-deny); set `access: "public"` to allow any signed-in
     * user. Enforced centrally by `RouteGuard` in this layout.
     */
    access?: "public";
    /**
     * Access Management page(s) this route belongs to. RouteGuard shows Access Denied unless
     * the user's role has the page (any one of them, if several). The deepest route that
     * declares it wins, so a child route can override its parent.
     */
    page?: PageKey | readonly PageKey[];
  }
}

/**
 * Pathless layout guarding every authenticated route. `beforeLoad` ensures the
 * current user is loaded (cached via React Query); a thrown error (401)
 * redirects to `/login`. The component renders the full app chrome.
 */
export const Route = createFileRoute("/_authenticated")({
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQueryOptions);
    } catch (err) {
      // Confirmed session expiry (401) redirects to login. Other errors,
      // including 403 and transient failures, show the recovery screen after
      // the query exhausts its retries.
      if (err instanceof AuthError) {
        throw redirect({ to: "/login" });
      }
      throw err;
    }
  },
  errorComponent: SessionLoadError,
  component: AppShell,
});

/**
 * Shown when loading the session fails for a non-auth reason (network / 5xx)
 * after the `me` query has exhausted its retries. Lets the user retry in place
 * instead of being redirected to login. Mirrors the old Next AuthWrapper's
 * "Unable to load your session / Try again / Relogin" recovery screen.
 */
function SessionLoadError({ reset }: ErrorComponentProps) {
  const navigate = useNavigate();
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
      <div className="space-y-1">
        <h1 className="text-lg font-semibold">Unable to load your session</h1>
        <p className="max-w-md text-sm text-muted-foreground">
          Something went wrong reaching the server. Check your connection and try again.
        </p>
      </div>
      <div className="flex gap-2">
        <ButtonComponent variant="primary" onClick={() => reset()}>
          Try again
        </ButtonComponent>
        <ButtonComponent variant="outline" onClick={() => navigate({ to: "/login" })}>
          Back to login
        </ButtonComponent>
      </div>
    </div>
  );
}

// Drives the Copilot panel from the shared CopilotProvider state so the Header's
// toggle (Cmd/Ctrl+I) and the panel stay in sync.
function CopilotHost() {
  const { isOpen, closeCopilot } = useCopilot();
  return <CopilotPanel isOpen={isOpen} onClose={closeCopilot} />;
}

/**
 * Default-deny access gate for the authenticated area. A route is admin-only
 * unless it declares `staticData.access === "public"`. Centralizing this means
 * a newly added route is protected by default — protection can't be forgotten
 * the way a per-route wrapper can. Non-admins are sent to `/unauthorized`
 * (itself public, so this never loops). Mirrors the old PageProtector's
 * client-side semantics, sourced from the same `useAuth`/`me` cache.
 */
function RouteGuard({ children }: { children: ReactNode }) {
  const isPublic = useMatches({
    select: (matches) => matches.some((m) => m.staticData.access === "public"),
  });
  // Joined to a string so the selector result is stable across renders.
  const pageKey = useMatches({
    select: (matches) => {
      const declared = matches.findLast((m) => m.staticData.page !== undefined)?.staticData.page;
      if (declared === undefined) {
        return "";
      }
      return typeof declared === "string" ? declared : declared.join(",");
    },
  });
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();

  const denied = !isPublic && !loading && !!user && !isAdmin;
  useEffect(() => {
    if (denied) navigate({ to: "/unauthorized", replace: true });
  }, [denied, navigate]);

  // While the user resolves, or when an admin-only route is denied, render
  // nothing in the content slot (the chrome stays); the effect redirects.
  if (!isPublic && (loading || (!!user && !isAdmin))) return null;
  if (pageKey) {
    return <PageAccessGate page={pageKey.split(",") as PageKey[]}>{children}</PageAccessGate>;
  }
  return <>{children}</>;
}

function AppShell() {
  useSyncSentryUser();
  useNavigationTracker();

  // Keep-alive calls the shared recovery coordinator. Confirmed expiry emits
  // pd:session-ended; GlobalErrorHandler clears the session and navigates to login.
  // Always enabled here: beforeLoad guarantees the user has resolved.
  useSessionKeepAlive(true);

  // `/emails`, `/support` and `/logs/track` render edge-to-edge; every other route gets the
  // shared page padding below (no centered max-width container - just
  // consistent horizontal + top spacing so titles clear the fixed header).
  const { pathname } = useLocation();
  const isFullWidth =
    pathname === "/emails" ||
    pathname === "/support" ||
    pathname === "/support/search" ||
    pathname === "/search" ||
    pathname === "/fundraising/emails" ||
    pathname === "/sales" ||
    pathname === "/granola" ||
    pathname === "/mail" ||
    pathname === "/logs/track" ||
    isSheetDetailPath(pathname) ||
    pathname === "/investor-updates" ||
    pathname.startsWith("/investor-updates/");

  // The Sheets grid (design 1b HUD) brings its own header bar instead of the app one.
  const isImmersive = isSheetDetailPath(pathname);

  return (
    <CopilotProvider>
      <GlobalErrorHandler>
        <SidebarProvider defaultOpen={false}>
          <SubrailSlotProvider>
            <Sidebar />
            <SidebarInset>
              <ChatBanner />
              {/* Header is fixed; offset the scrollable content by its measured height. */}
              {!isImmersive && <Header />}
              <div
                className={cn(
                  "flex min-h-0 flex-1 flex-col overflow-x-clip",
                  !isImmersive && "pt-[var(--header-height,3.5rem)]",
                )}
              >
                <MobileSubnav />
                <div className="flex min-h-0 min-w-0 w-full flex-1 items-start">
                  <div
                    className={cn(
                      "h-full min-h-0 min-w-0 w-full max-w-full",
                      !isFullWidth && "px-4 pt-8 pb-4 lg:px-6",
                    )}
                  >
                    <PageTransition>
                      <div className="grid min-h-0 min-w-0 gap-4 w-full max-w-full">
                        <RouteGuard>
                          <Outlet />
                        </RouteGuard>
                      </div>
                    </PageTransition>
                  </div>
                </div>
              </div>
            </SidebarInset>
            <CopilotHost />
            <Toaster richColors position="top-right" />
          </SubrailSlotProvider>
        </SidebarProvider>
      </GlobalErrorHandler>
    </CopilotProvider>
  );
}
